import React, { useState, useEffect, useCallback } from "react";
import { fmtVnTime } from "../../utils/format";
import { useUI } from "../../context/UIContext";
import { deleteAlert, getAlertsPaged } from "../../api";
import AddAppModal from "../modals/AddAppModal";
import EditAppPidModal from "../modals/EditAppPidModal";
import DeleteAppModal from "../modals/DeleteAppModal";
import ClearAlertsModal from "../modals/ClearAlertsModal";
import Pm2LogModal from "../modals/Pm2LogModal";

function AlertRow({ a, onDelete, canDelete }) {
  const isCrit = a.severity === "critical";
  const isWarn = a.severity === "warn";
  
  // Phong cách thiết kế mới cho dòng cảnh báo (có border trái, background soft và icon tương ứng)
  const leftBorderColor = isCrit ? "var(--red)" : isWarn ? "var(--amber)" : "var(--blue)";
  const rowBg = isCrit 
    ? "rgba(248, 113, 113, 0.06)" 
    : isWarn 
      ? "rgba(251, 191, 36, 0.05)" 
      : "rgba(96, 165, 250, 0.05)";
  const icon = isCrit ? "✕" : isWarn ? "!" : "i";
  const iconColor = isCrit ? "var(--red)" : isWarn ? "var(--amber)" : "var(--blue)";
  const time = fmtVnTime(a.createdDate);

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "13px",
      padding: "12px 18px",
      borderBottom: "1px solid var(--border)",
      borderLeft: `3px solid ${leftBorderColor}`,
      background: rowBg,
    }}>
      <span style={{ width: "18px", textAlign: "center", color: iconColor, fontWeight: 700 }}>
        {icon}
      </span>
      <span style={{ flex: 1, fontSize: "13px", fontFamily: "var(--font-mono)", color: isCrit ? "var(--red)" : "var(--text)" }}>
        <strong>{a.serverCode || ""}</strong> · {a.message || ""}
      </span>
      <span style={{ fontSize: "12px", color: "var(--faint)", fontFamily: "var(--font-mono)", whiteSpace: "nowrap" }}>{time}</span>
      {canDelete && a.logId != null && (
        <button
          title="Xoá cảnh báo này"
          style={{
            width: "26px",
            height: "26px",
            borderRadius: "7px",
            background: "transparent",
            border: "1px solid var(--border2)",
            color: "var(--dim)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginLeft: "8px",
            transition: "all 0.2s ease",
          }}
          className="alert-del-btn"
          onClick={() => onDelete(a)}
        >
          ✕
        </button>
      )}
    </div>
  );
}

export default function AppsAlertsView({
  servers,
  pm2Servers = [],
  onPm2Action,
  onProcessAction,
  alerts,
  canOperate,
  isAdmin,
  searchTerm = "",
}) {
  const { openModal, toast } = useUI();

  // ── Bộ lọc thời gian + phân trang cho danh sách cảnh báo ───────────────
  const [range, setRange] = useState("24h"); // 1h | 24h | 7d | all | custom
  const [severity, setSeverity] = useState(""); // "" | warn | critical
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [paged, setPaged] = useState({ rows: [], total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  // Quy đổi preset khoảng thời gian → mốc from/to (ISO)
  const resolveRange = useCallback(() => {
    if (range === "all") return { from: null, to: null };
    if (range === "custom") {
      return {
        from: customFrom ? new Date(customFrom).toISOString() : null,
        to: customTo ? new Date(customTo).toISOString() : null,
      };
    }
    const ms = { "1h": 3600e3, "24h": 86400e3, "7d": 7 * 86400e3 }[range];
    return { from: new Date(Date.now() - ms).toISOString(), to: null };
  }, [range, customFrom, customTo]);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const { from, to } = resolveRange();
      const data = await getAlertsPaged({
        page,
        pageSize,
        from,
        to,
        severity: severity || null,
      });
      setPaged({
        rows: data.rows || [],
        total: data.total || 0,
        totalPages: data.totalPages || 1,
      });
    } catch {
      setPaged({ rows: [], total: 0, totalPages: 1 });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, severity, resolveRange]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage, alerts]);

  useEffect(() => {
    setPage(1);
  }, [range, severity, customFrom, customTo, pageSize]);

  const handleDeleteOne = async (a) => {
    try {
      const { ok, data } = await deleteAlert(a.logId);
      toast(ok ? "Đã xoá cảnh báo" : data.message || "Lỗi server", ok ? "ok" : "err");
    } catch {
      toast("Không kết nối được BackendNode", "err");
    }
  };

  // Gộp 2 nguồn vào 1 danh sách: app thường (sv.apps, type='process') + app PM2
  const appRows = [];
  const term = searchTerm.trim().toLowerCase();

  Object.values(servers).forEach((sv) => {
    (sv.apps || []).forEach((app, i) => {
      const match = !term ||
        (sv.ServerCode || "").toLowerCase().includes(term) ||
        (app.appName || "").toLowerCase().includes(term);
      if (match) {
        appRows.push({
          serverCode: sv.ServerCode,
          type: "process",
          app: {
            appName: app.appName,
            isRunning: app.isRunning,
            pid: app.pid,
            cpuUsage: app.cpuUsage,
            memMB: app.memMB,
          },
          key: `proc-${sv.ServerCode}-${app.appName}-${i}`,
        });
      }
    });
  });
  pm2Servers.forEach((srv) => {
    (srv.processes || []).forEach((p, i) => {
      const match = !term ||
        (srv.serverCode || "").toLowerCase().includes(term) ||
        (p.name || "").toLowerCase().includes(term);
      if (match) {
        appRows.push({
          serverCode: srv.serverCode,
          type: "pm2",
          app: {
            appName: p.name,
            isRunning: p.status === "online",
            status: p.status,
            pid: p.pid,
            cpuUsage: Number(p.cpuPct) || 0,
            memMB: Number(p.memMB) || 0,
          },
          key: `pm2-${srv.serverCode}-${p.name}-${i}`,
        });
      }
    });
  });

  const filteredAlertRows = paged.rows.filter((a) => {
    if (!term) return true;
    const msg = (a.message || "").toLowerCase();
    const sv = (a.serverCode || "").toLowerCase();
    return msg.includes(term) || sv.includes(term);
  });

  const total = appRows.length;
  const run = appRows.filter((r) => r.app.isRunning).length;
  const down = total - run;
  const critCount = alerts.filter((a) => a.severity === "critical").length;

  return (
    <div>
      {/* Overview Cards */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
        gap: "14px",
        marginBottom: "20px",
      }} className="apps-stats-grid">
        {/* Monitored Apps */}
        <div style={{ padding: "16px", borderRadius: "14px", background: "var(--surface)", border: "1px solid var(--border)" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, letterSpacing: ".6px", textTransform: "uppercase", color: "var(--faint)", marginBottom: "12px" }}>Monitored apps</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "30px", fontWeight: 700, lineHeight: 1, color: "var(--text)" }}>{total}</div>
          <div style={{ fontSize: "12px", color: "var(--dim)", marginTop: "6px" }}>across {Object.keys(servers).length} servers</div>
        </div>

        {/* Running Apps */}
        <div style={{ padding: "16px", borderRadius: "14px", background: "var(--surface)", border: "1px solid var(--border)" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, letterSpacing: ".6px", textTransform: "uppercase", color: "var(--faint)", marginBottom: "12px" }}>Running</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "30px", fontWeight: 700, lineHeight: 1, color: "var(--green)" }}>{run}</div>
          <div style={{ fontSize: "12px", color: "var(--dim)", marginTop: "6px" }}>healthy processes</div>
        </div>

        {/* Down Apps */}
        <div style={{ padding: "16px", borderRadius: "14px", background: "var(--surface)", border: "1px solid var(--border)" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, letterSpacing: ".6px", textTransform: "uppercase", color: "var(--faint)", marginBottom: "12px" }}>Down</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "30px", fontWeight: 700, lineHeight: 1, color: "var(--red)" }}>{down}</div>
          <div style={{ fontSize: "12px", color: "var(--dim)", marginTop: "6px" }}>not responding</div>
        </div>

        {/* Critical Alerts */}
        <div style={{ padding: "16px", borderRadius: "14px", background: "var(--surface)", border: "1px solid var(--border)" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, letterSpacing: ".6px", textTransform: "uppercase", color: "var(--faint)", marginBottom: "12px" }}>Critical alerts</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "30px", fontWeight: 700, lineHeight: 1, color: "var(--amber)" }}>{critCount}</div>
          <div style={{ fontSize: "12px", color: "var(--dim)", marginTop: "6px" }}>last 24 hours</div>
        </div>
      </div>

      {/* Application Status Panel */}
      <div style={{
        borderRadius: "14px",
        background: "var(--surface)",
        border: "1px solid var(--border)",
        overflow: "hidden",
        marginBottom: "16px",
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 18px",
          borderBottom: "1px solid var(--border)",
        }}>
          <div style={{ fontSize: "14px", fontWeight: 700, color: "var(--text)" }}>Application status</div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "11.5px", fontFamily: "var(--font-mono)", color: "var(--faint)" }}>real-time · Socket.IO</span>
            {canOperate && (
              <button
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "0 13px",
                  height: "34px",
                  borderRadius: "9px",
                  background: "var(--primary)",
                  border: "none",
                  color: "#fff",
                  fontSize: "12.5px",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                className="panel-action-btn"
                onClick={() =>
                  openModal(
                    "＋ Thêm app giám sát",
                    <AddAppModal servers={servers} />
                  )
                }
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Add app
              </button>
            )}
          </div>
        </div>

        <div style={{ overflowX: "auto" }} className="scroll">
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "760px" }}>
            <thead>
              <tr>
                <th style={thStyle}>Server</th>
                <th style={thStyle}>App</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>PID</th>
                <th style={thStyle}>CPU</th>
                <th style={thStyle}>RAM</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {appRows.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{
                    textAlign: "center",
                    padding: "24px",
                    color: "var(--faint)",
                    fontFamily: "var(--font-mono)",
                    fontSize: "13px",
                  }}>
                    Đang tải danh sách ứng dụng...
                  </td>
                </tr>
              ) : (
                appRows.map(({ serverCode, app, type, key }) => {
                  const isRunning = app.isRunning;
                  const isPm2 = type === "pm2";
                  
                  // Console log handler
                  const handleOpenLogs = () => {
                    openModal(
                      `Console · ${app.appName}`,
                      <Pm2LogModal serverCode={serverCode} name={app.appName} />,
                      "wide"
                    );
                  };

                  return (
                    <tr key={key} className="table-row-hover">
                      <td style={{ ...tdStyle, fontWeight: 700, color: "var(--primary)", fontFamily: "var(--font-mono)" }}>
                        {serverCode}
                      </td>
                      <td style={{ ...tdStyle, color: "var(--text)", fontWeight: 500 }}>
                        {app.appName}
                      </td>
                      <td style={tdStyle}>
                        <span style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: "11px",
                          fontWeight: 700,
                          padding: "2px 8px",
                          borderRadius: "6px",
                          color: isPm2 ? "var(--green)" : "var(--dim)",
                          background: isPm2 ? "var(--green-soft)" : "var(--surface2)",
                          border: isPm2 ? "none" : "1px solid var(--border2)",
                        }}>
                          {isPm2 ? "node · pm2" : "process"}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <span style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          fontFamily: "var(--font-mono)",
                          fontSize: "12px",
                          fontWeight: 700,
                          color: isRunning ? "var(--green)" : "var(--red)",
                        }}>
                          <span style={{
                            width: "7px",
                            height: "7px",
                            borderRadius: "50%",
                            background: isRunning ? "var(--green)" : "var(--red)",
                          }}></span>
                          {isRunning ? "Running" : "Down"}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, fontFamily: "var(--font-mono)", color: "var(--dim)" }}>
                        {isRunning && app.pid ? app.pid : "—"}
                      </td>
                      <td style={{
                        ...tdStyle,
                        fontFamily: "var(--font-mono)",
                        color: !isRunning ? "var(--faint)" : app.cpuUsage > 50 ? "var(--amber)" : "var(--green)",
                        fontWeight: isRunning ? 600 : 400,
                      }}>
                        {isRunning && app.cpuUsage != null ? `${Number(app.cpuUsage).toFixed(1)}%` : "—"}
                      </td>
                      <td style={{ ...tdStyle, fontFamily: "var(--font-mono)", color: isRunning ? "var(--dim)" : "var(--faint)" }}>
                        {isRunning && app.memMB ? `${Number(app.memMB).toFixed(0)} MB` : "—"}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                        {!canOperate ? (
                          <span style={{ color: "var(--faint)", fontSize: "12px" }}>—</span>
                        ) : (
                          <span style={{ display: "inline-flex", gap: "5px" }}>
                            {/* Nút Log console */}
                            <button
                              onClick={handleOpenLogs}
                              title="Console Logs"
                              style={actionBtnStyle}
                              className="act-btn"
                            >
                              ▤
                            </button>
                            
                            {/* Restart */}
                            <button
                              onClick={() => isPm2 ? onPm2Action?.(serverCode, app.appName, "restart") : onProcessAction?.(serverCode, app.appName, "restart")}
                              title="Restart App"
                              style={actionBtnStyle}
                              className="act-btn"
                            >
                              ↻
                            </button>
                            
                            {/* Start / Stop */}
                            {isRunning ? (
                              <button
                                onClick={() => isPm2 ? onPm2Action?.(serverCode, app.appName, "stop") : onProcessAction?.(serverCode, app.appName, "stop")}
                                title="Stop App"
                                style={{ ...actionBtnStyle, color: "var(--red)" }}
                                className="act-btn-danger"
                              >
                                ■
                              </button>
                            ) : (
                              <button
                                onClick={() => isPm2 ? onPm2Action?.(serverCode, app.appName, "start") : onProcessAction?.(serverCode, app.appName, "start")}
                                title="Start App"
                                style={{
                                  ...actionBtnStyle,
                                  background: "var(--green-soft)",
                                  borderColor: "var(--green-soft)",
                                  color: "var(--green)",
                                  fontWeight: 700,
                                }}
                                className="panel-action-btn"
                              >
                                ▶
                              </button>
                            )}

                            {/* Cấu hình thêm cho app thường */}
                            {!isPm2 && (
                              <>
                                <button
                                  onClick={() =>
                                    openModal(
                                      `✎ Sửa · ${app.appName}`,
                                      <EditAppPidModal
                                        serverCode={serverCode}
                                        appName={app.appName}
                                      />
                                    )
                                  }
                                  title="Sửa PID / Lệnh khởi chạy"
                                  style={actionBtnStyle}
                                  className="act-btn"
                                >
                                  ✎
                                </button>
                                <button
                                  onClick={() =>
                                    openModal(
                                      `✕ Xoá app · ${app.appName}`,
                                      <DeleteAppModal
                                        serverCode={serverCode}
                                        appName={app.appName}
                                      />
                                    )
                                  }
                                  title="Xoá app"
                                  style={{ ...actionBtnStyle, color: "var(--red)" }}
                                  className="act-btn-danger"
                                >
                                  ✕
                                </button>
                              </>
                            )}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* System Alerts Panel */}
      <div style={{
        borderRadius: "14px",
        background: "var(--surface)",
        border: "1px solid var(--border)",
        overflow: "hidden",
      }}>
        {/* Alerts Header */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 18px",
          borderBottom: "1px solid var(--border)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--amber)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            <span style={{ fontSize: "14px", fontWeight: 700, color: "var(--text)" }}>System alerts</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "11.5px", fontFamily: "var(--font-mono)", color: "var(--faint)" }}>
              {loading ? "loading..." : `${paged.total} alerts`}
            </span>
            {isAdmin && (
              <button
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "0 13px",
                  height: "34px",
                  borderRadius: "9px",
                  background: "transparent",
                  border: "1px solid var(--border2)",
                  color: "var(--red)",
                  fontSize: "12.5px",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                className="clear-alerts-btn"
                onClick={() =>
                  openModal(
                    "✕ Xoá log theo máy chủ",
                    <ClearAlertsModal servers={servers} />
                  )
                }
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                </svg>
                Clear logs
              </button>
            )}
          </div>
        </div>

        {/* Filter Bar */}
        <div style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "8px",
          padding: "12px 18px",
          borderBottom: "1px solid var(--border)",
        }}>
          <span style={{ fontSize: "12px", color: "var(--faint)", fontFamily: "var(--font-mono)", marginRight: "4px" }}>Range:</span>
          {[
            { v: "1h", t: "1h" },
            { v: "24h", t: "24h" },
            { v: "7d", t: "7d" },
            { v: "all", t: "All" },
            { v: "custom", t: "Custom" },
          ].map((o) => {
            const isSel = range === o.v;
            return (
              <button
                key={o.v}
                onClick={() => setRange(o.v)}
                style={{
                  padding: "5px 12px",
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: 600,
                  cursor: "pointer",
                  border: isSel ? "none" : "1px solid var(--border2)",
                  background: isSel ? "var(--primary)" : "transparent",
                  color: isSel ? "#fff" : "var(--dim)",
                  transition: "all 0.15s ease",
                }}
                className="filter-pill-btn"
              >
                {o.t}
              </button>
            );
          })}

          {range === "custom" && (
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "6px" }}>
              <input
                type="datetime-local"
                style={filterInputStyle}
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
              <span style={{ fontSize: "12px", color: "var(--faint)" }}>→</span>
              <input
                type="datetime-local"
                style={filterInputStyle}
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
              />
            </div>
          )}

          <span style={{ fontSize: "12px", color: "var(--faint)", fontFamily: "var(--font-mono)", margin: "0 4px 0 10px" }}>Severity:</span>
          <select
            style={{
              padding: "5px 12px",
              borderRadius: "8px",
              fontSize: "12px",
              fontWeight: 600,
              background: "var(--surface2)",
              border: "1px solid var(--border2)",
              color: "var(--text)",
              outline: "none",
              cursor: "pointer",
            }}
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
          >
            <option value="">All</option>
            <option value="critical">Critical</option>
            <option value="warn">Warning</option>
          </select>
        </div>

        {/* Alerts Rows List */}
        <div>
          {loading ? (
            <div style={emptyBoxStyle}>Đang tải...</div>
          ) : filteredAlertRows.length === 0 ? (
            <div style={emptyBoxStyle}>Không có cảnh báo nào.</div>
          ) : (
            filteredAlertRows.map((a, i) => (
              <AlertRow
                key={a.logId || a.id || i}
                a={a}
                onDelete={handleDeleteOne}
                canDelete={isAdmin}
              />
            ))
          )}
        </div>

        {/* Table/Alerts Pagination Footer */}
        {paged.total > 0 && (
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            padding: "12px 18px",
            borderTop: "1px solid var(--border)",
            flexWrap: "wrap",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "12px", color: "var(--faint)" }}>Mỗi trang:</span>
              <select
                style={{
                  padding: "4px 8px",
                  borderRadius: "6px",
                  fontSize: "12px",
                  background: "var(--surface2)",
                  border: "1px solid var(--border2)",
                  color: "var(--text)",
                  outline: "none",
                  cursor: "pointer",
                }}
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "12px", color: "var(--faint)", fontFamily: "var(--font-mono)" }}>
                Page {page} / {paged.totalPages} · {paged.total} rows
              </span>
              <button
                style={{
                  padding: "6px 12px",
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: 600,
                  background: page <= 1 ? "transparent" : "var(--surface2)",
                  border: "1px solid var(--border2)",
                  color: page <= 1 ? "var(--faint)" : "var(--text)",
                  cursor: page <= 1 ? "not-allowed" : "pointer",
                }}
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
              >
                ‹ Prev
              </button>
              <button
                style={{
                  padding: "6px 12px",
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: 600,
                  background: page >= paged.totalPages ? "transparent" : "var(--surface2)",
                  border: "1px solid var(--border2)",
                  color: page >= paged.totalPages ? "var(--faint)" : "var(--text)",
                  cursor: page >= paged.totalPages ? "not-allowed" : "pointer",
                }}
                disabled={page >= paged.totalPages}
                onClick={() => setPage((p) => Math.min(p + 1, paged.totalPages))}
              >
                Next ›
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .panel-action-btn:hover {
          filter: brightness(1.07);
        }
        .clear-alerts-btn:hover {
          background: var(--red-soft) !important;
        }
        .table-row-hover:hover {
          background: var(--hover);
        }
        .act-btn {
          width: 28px;
          height: 28px;
          border-radius: 7px;
          background: transparent;
          border: 1px solid var(--border2);
          color: var(--dim);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          transition: all 0.2s ease;
        }
        .act-btn:hover {
          color: var(--primary) !important;
          border-color: var(--primary) !important;
        }
        .act-btn-danger {
          width: 28px;
          height: 28px;
          border-radius: 7px;
          background: transparent;
          border: 1px solid var(--border2);
          color: var(--dim);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          transition: all 0.2s ease;
        }
        .act-btn-danger:hover {
          color: var(--red) !important;
          border-color: var(--red) !important;
        }
        .alert-del-btn:hover {
          color: var(--red) !important;
          border-color: var(--red) !important;
        }
        .filter-pill-btn:hover:not(:disabled) {
          border-color: var(--dim) !important;
        }
        @media (max-width: 1024px) {
          .apps-stats-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
        }
      `}</style>
    </div>
  );
}

const thStyle = {
  textAlign: "left",
  fontSize: "10.5px",
  fontWeight: "700",
  letterSpacing: ".8px",
  textTransform: "uppercase",
  color: "var(--faint)",
  padding: "10px 18px",
  background: "var(--surface2)",
  borderBottom: "1px solid var(--border)",
};

const tdStyle = {
  padding: "12px 18px",
  borderBottom: "1px solid var(--border)",
  fontSize: "13px",
  verticalAlign: "middle",
};

const filterInputStyle = {
  padding: "5px 10px",
  fontSize: "12px",
  background: "var(--surface2)",
  border: "1px solid var(--border2)",
  borderRadius: "8px",
  color: "var(--text)",
  outline: "none",
  fontFamily: "var(--font-mono)",
};

const emptyBoxStyle = {
  padding: "24px 18px",
  fontSize: "13px",
  fontFamily: "var(--font-mono)",
  color: "var(--faint)",
  textAlign: "center",
};

const actionBtnStyle = {};

